// @flow
import VisuallyHidden from "@reach/visually-hidden";
import { Box } from "grid-styled";
import PropTypes from "prop-types";
import { Component, Fragment } from "react";
import { withTranslation } from "react-i18next";

import { withLabelContext } from "../../context_providers/LabelContext";
import { withTenantContext } from "../../context_providers/TenantContext";
import { withViewer } from "../../context_providers/ViewerContext";
import { getRootUrl } from "../../utils/urls";
import { ContentContainerFlex } from "../pages";

import Welcome from "./Welcome";

import { H1 } from "pstat-design-system/text";
import { PolicyTable } from "pstat-design-system/tables";
import { Buckets as NewAndRecentlyRevisedBuckets } from "pstat-anywhere/components/document_reports/revised_recent/NewAndRecentlyRevisedReport";
import NewAndRecentlyRevisedCSVQuery from "pstat-anywhere/components/document_reports/revised_recent/NewAndRecentlyRevisedCSVQuery";
import { Buckets as DocumentsForReviewBuckets } from "pstat-anywhere/components/document_reports/expiring/DocumentsForReviewReport";
import { buildPolicyTableStructure } from "pstat-anywhere/components/acknowledgments/mine/MyPoliciesToAcknowledgeReport";

class _HomeContent extends Component {
  shouldShowWelcomeMessage = () => {
    return (
      this.props.currentTenant.settings.customWelcomeContentShow &&
      this.props.currentTenant.settings.customWelcomeContentPsa
    );
  };
  advancedT = tableName => {
    // Simple helper to reduce code.
    // Translate correct title and label for PolicyTable
    const { t, labels } = this.props;
    const { documentLabelPlural } = labels;
    return {
      title: t(`home.tables.${tableName}.title`, { documentLabelPlural }),
      label: t(`home.tables.${tableName}.label`, { documentLabelPlural }),
      noContent: t(`home.tables.${tableName}.noContent`, {
        documentLabelPlural
      })
    };
  };

  render() {
    const {
      columnHeaders,
      myDocumentsToAcknowledge,
      documentsToReview,
      pendingDocuments,
      recentlyRevisedDocuments,
      currentTenant,
      t,
      viewer,
      tenant
    } = this.props;

    return (
      <ContentContainerFlex wrap px={[3, 3, 0]}>
        <VisuallyHidden>
          <H1>{t("home.homeContent.header")}</H1>
        </VisuallyHidden>
        <Box p={[0, 0, 3]}>
          {this.shouldShowWelcomeMessage() && (
            <Welcome content={currentTenant.settings.customWelcomeContentPsa} />
          )}
          {!viewer.isGuest && (
            <Fragment>
              {/* Approval Table */}
              <PolicyTable
                policies={pendingDocuments.documents.edges.map(
                  edge => edge.node
                )}
                totalCount={pendingDocuments.documents.totalCount}
                headingContent={this.advancedT("approve")}
                tableType="approve"
                link={`${getRootUrl()}/policy_reports/awaiting_activation/pending`}
                columnHeaders={columnHeaders}
              />
              {/* Review Table */}
              <PolicyTable
                policies={documentsToReview.documents.edges.map(
                  edge => edge.node
                )}
                totalCount={documentsToReview.documents?.totalCount}
                headingContent={this.advancedT("review")}
                tableType="review"
                link={`${getRootUrl()}/policy_reports/expiring`}
                columnHeaders={columnHeaders}
                BucketsComponent={() => (
                  <DocumentsForReviewBuckets
                    buckets={documentsToReview.buckets}
                  />
                )}
              />
              {/* Acknowledgement Table */}
              {!tenant.enableAllBlocs && (
                <PolicyTable
                  tableType="my_policies_to_acknowledge_home_page"
                  policies={buildPolicyTableStructure(myDocumentsToAcknowledge)}
                  totalCount={myDocumentsToAcknowledge.totalCount}
                  headingContent={this.advancedT("acknowledge")}
                  link={`${getRootUrl()}/acknowledgment/mine/`}
                  columnHeaders={columnHeaders}
                />
              )}
            </Fragment>
          )}
          {/* New & Recently Revised */}
          <PolicyTable
            policies={recentlyRevisedDocuments.documents.edges.map(
              edge => edge.node
            )}
            tableType="revised_recent_home_page"
            headingContent={this.advancedT("revised")}
            link={`${getRootUrl()}/policy_reports/revised/recent`}
            csvQuery={NewAndRecentlyRevisedCSVQuery}
            columnHeaders={columnHeaders}
            totalCount={recentlyRevisedDocuments.documents?.totalCount}
            BucketsComponent={() => (
              <NewAndRecentlyRevisedBuckets
                buckets={recentlyRevisedDocuments.buckets}
              />
            )}
          />
        </Box>
      </ContentContainerFlex>
    );
  }
}

_HomeContent.propTypes = {
  title: PropTypes.string.isRequired,
  columnHeaders: PropTypes.arrayOf(PropTypes.string).isRequired,
  data: PropTypes.object
};

_HomeContent.defaultProps = {
  columnHeaders: ["Title", "Effective"]
};

export const HomeContent = withTranslation()(_HomeContent);
export default withViewer(withLabelContext(withTenantContext(HomeContent)));
